import { OrderUpdate } from "@commercelayer/sdk";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { parseAndThrowPaymentError } from "@/lib/backend-error";
import { OrderResponse } from "../types/order";
import { CreateOrderRequest, CreateOrderResponse } from "../types/create-order";
import { RootState } from "../store";
import { t } from "i18next";

export const fetchOrder = createAsyncThunk(
  "order/fetchOrder",
  async (orderId: string) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/orders/${orderId}`,
      {
        credentials: 'include', // Send cookies for partner attribution
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch order");
    }

    return response.json() as Promise<OrderResponse>;
  }
);

interface AddToCartPayload {
  orderId: string;
  skuCode: string;
  quantity?: number;
  updateQuantity?: boolean;
  metadata?: Record<string, string>;
}

interface AddToCartResponse {
  success: boolean;
  lineItemId: string;
  quantity: number;
  totalAmount: number;
  metadata?: Record<string, string>;
}

export const addToCart = createAsyncThunk(
  "order/addToCart",
  async (data: AddToCartPayload, { getState }) => {
    const state = getState() as RootState;
    const id = state.order.fetchedOrder.data?.id;

    if (!id) {
      throw new Error("Failed to add to cart: no order ID");
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/orders/${id}/add-to-cart`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to add to cart");
    }

    return response.json() as Promise<AddToCartResponse>;
  }
);

export const finalizeOrder = createAsyncThunk(
  "order/finalizeOrder",
  async (data: CreateOrderRequest) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/create-order`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      parseAndThrowPaymentError(
        errorData,
        errorData?.message || t("errors.FAILED_TO_CREATE_ORDER")
      );
    }

    return response.json() as Promise<CreateOrderResponse>;
  }
);

export interface UpdateOrderRequest {
  orderId: string;
  attributes: Partial<OrderUpdate>;
}

export const updateOrder = createAsyncThunk(
  "order/updateOrder",
  async ({ orderId, attributes }: UpdateOrderRequest) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/orders/${orderId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include',
        body: JSON.stringify({ attributes }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update order");
    }

    return response.json() as Promise<OrderResponse>;
  }
);
